// AI策略大模型服务：状态查询与对话
// 所有请求经 /api 代理到快推 AI 服务端；API Key 只在服务端，前端不可见

import { ApiError, getToken, clearToken } from './server-api';

export interface AIStatus {
  configured: boolean;
  model: string;
  remaining_questions: number;
  question_limit: number;
}

export interface CreativeSection {
  key: string;
  title: string;
  text: string;
  collapsible: boolean;
  copyable: boolean;
}

export interface CreativePayload {
  task: string;
  label: string;
  payload: Record<string, unknown>;
  sections: CreativeSection[];
}

export interface AIChatResult {
  success: boolean;
  content: string;
  model: string;
  remaining_questions: number;
  code?: string;   // QUESTION_LIMIT_REACHED / AI_PROVIDER_ERROR / AI_NOT_CONFIGURED
  message?: string;
  creative?: CreativePayload | null; // 编导创意任务的结构化结果（普通对话为 null/缺省）
}

const authedFetch = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const res = await fetch(`/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    // 登录过期：清 token 刷新回登录页
    localStorage.removeItem('kk_token');
    window.location.reload();
    throw new ApiError('未登录或登录已过期');
  }

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError((body && (body.message || body.error)) || `HTTP ${res.status}`, body && body.code);
  }
  return body as T;
};

// 模型是否已配置 + 当前用户剩余额度
export const getAIStatus = () =>
  authedFetch<AIStatus>('/ai/status');

// 发送提问（服务端校验额度 → 构建System Prompt → 调用大模型 → 成功才扣减）
export const sendAIChat = (payload: {
  project_id: string;
  role: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  project_context: Record<string, unknown>;
}) => authedFetch<AIChatResult>('/ai/chat', {
  method: 'POST',
  body: JSON.stringify(payload),
});

// ---------- 流式提问（NDJSON 事件：ack/stage/delta/final/error） ----------
// POST + fetch ReadableStream（不用 EventSource，避免 GET 参数限制）
// - onDelta：普通文本回答的增量片段（结构化任务不会有 delta）
// - onStage：真实执行阶段更新（Router/Context/生成/渲染）
// - 返回 final.data（与 /api/ai/chat 响应结构一致，另含 response_mode/elapsed_ms）
// - 可通过 abortSignal 取消（取消不扣次数由后端保证：未 final 不扣）

export interface AIChatStreamHandlers {
  onAck?: (event: { taskType: string; responseMode: string }) => void;
  onStage?: (event: { stage: string; message: string; elapsed?: number }) => void;
  onDelta?: (piece: string) => void;
}

export const sendAIChatStream = async (
  payload: {
    project_id: string;
    role: string;
    messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    project_context: Record<string, unknown>;
  },
  handlers: AIChatStreamHandlers = {},
  abortSignal?: AbortSignal,
): Promise<AIChatResult> => {
  const res = await fetch(`/api/ai/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: JSON.stringify(payload),
    signal: abortSignal,
  });

  if (res.status === 401) {
    clearToken();
    window.location.reload();
    throw new ApiError('未登录或登录已过期');
  }
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => null);
    throw new ApiError((body && body.error) || `HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let finalData: AIChatResult | null = null;
  let streamError: ApiError | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(trimmed); } catch { continue; }
      if (ev.type === 'ack') {
        handlers.onAck?.(ev as { taskType: string; responseMode: string });
      } else if (ev.type === 'stage') {
        handlers.onStage?.(ev as { stage: string; message: string; elapsed?: number });
      } else if (ev.type === 'delta') {
        handlers.onDelta?.(String(ev.content || ''));
      } else if (ev.type === 'final') {
        finalData = ev.data as AIChatResult;
      } else if (ev.type === 'error') {
        const code = String(ev.code || 'AI_ERROR');
        streamError = new ApiError(String(ev.message || 'AI服务暂时不可用，本次未扣除提问次数。'), code);
        if (code === 'QUESTION_LIMIT_REACHED') {
          streamError = new ApiError(String(ev.message || '本期提问额度已用完'), code);
        }
      }
    }
  }

  if (finalData) return finalData;
  throw streamError || new ApiError('AI服务暂时不可用，本次未扣除提问次数。', 'AI_PROVIDER_ERROR');
};
